/**
 * Hit-FX config — combat juice (first-friend playtest 2026-08-02:
 * "hard to tell what happened in combat").
 *
 * Two-axis system: the MOVE'S TYPE picks the skin (which cartridge
 * battle-anim frames burst at the defender + how they move), the
 * EFFECTIVENESS picks the magnitude (count / speed / scale weights).
 * Identity-vs-state law: the skin IS the identity of the move's energy
 * — lifted cartridge pixels, never tinted into state surfaces; the
 * magnitude + hit-stop + shake carry state.
 *
 * Pure module — no Phaser. The engine (hitFx.ts) consumes the resolved
 * plan; tests pin the table's integrity against battleFxManifest.
 */
import { BATTLE_FX_MANIFEST } from '../data/battleFxManifest';

export const GEN3_TYPES = [
  'normal', 'fire', 'water', 'electric', 'grass', 'ice', 'fighting',
  'poison', 'ground', 'flying', 'psychic', 'bug', 'rock', 'ghost',
  'dragon', 'dark', 'steel',
] as const;
export type Gen3Type = (typeof GEN3_TYPES)[number];

/** Emitter param family — how the frames MOVE. Motion is ours; only
 *  the pixels are cartridge. */
export type FxMotion = 'burst' | 'rise' | 'fall' | 'slash' | 'ring' | 'spray';

export interface TypeFxSkin {
  /** battleFxManifest key — texture 'fx-<sheet>'. */
  sheet: string;
  motion: FxMotion;
  /** Neutral-tier particle count (magnitude scales it; hard cap 20). */
  count: number;
  /** Particle speed range, px/s. */
  speed: [number, number];
  /** Gravity px/s² (positive = down). */
  gravityY: number;
  /** Particle lifespan ms (also the burst's visual duration). */
  lifespan: number;
  /** Display scale of the frames (cartridge frames are 8-32px). */
  scale: number;
  /** Random spin, for shard/leaf-ish skins. */
  rotate?: boolean;
}

export const TYPE_FX: Record<Gen3Type, TypeFxSkin> = {
  normal: { sheet: 'impact', motion: 'burst', count: 6, speed: [40, 110], gravityY: 0, lifespan: 340, scale: 0.8 },
  fire: { sheet: 'ember', motion: 'rise', count: 9, speed: [30, 80], gravityY: -70, lifespan: 420, scale: 0.7 },
  water: { sheet: 'droplet', motion: 'fall', count: 9, speed: [60, 130], gravityY: 320, lifespan: 420, scale: 0.6 },
  electric: { sheet: 'spark', motion: 'burst', count: 12, speed: [90, 190], gravityY: 0, lifespan: 300, scale: 1.0 },
  grass: { sheet: 'leaf', motion: 'burst', count: 8, speed: [50, 120], gravityY: 90, lifespan: 430, scale: 0.9, rotate: true },
  ice: { sheet: 'ice-crystal', motion: 'fall', count: 8, speed: [40, 90], gravityY: 240, lifespan: 430, scale: 0.8, rotate: true },
  fighting: { sheet: 'punch', motion: 'burst', count: 5, speed: [30, 80], gravityY: 0, lifespan: 300, scale: 0.9 },
  poison: { sheet: 'poison-bubble', motion: 'rise', count: 8, speed: [20, 55], gravityY: -50, lifespan: 480, scale: 0.9 },
  ground: { sheet: 'mud', motion: 'spray', count: 12, speed: [70, 150], gravityY: 260, lifespan: 380, scale: 1.1 },
  flying: { sheet: 'gust', motion: 'slash', count: 6, speed: [110, 200], gravityY: 0, lifespan: 320, scale: 0.7 },
  psychic: { sheet: 'rainbow-ring', motion: 'ring', count: 10, speed: [70, 90], gravityY: 0, lifespan: 380, scale: 1.1 },
  bug: { sheet: 'pin', motion: 'burst', count: 9, speed: [80, 160], gravityY: 0, lifespan: 320, scale: 0.8, rotate: true },
  rock: { sheet: 'rocks', motion: 'fall', count: 7, speed: [50, 110], gravityY: 340, lifespan: 430, scale: 0.7, rotate: true },
  ghost: { sheet: 'shadow-ball', motion: 'rise', count: 9, speed: [15, 45], gravityY: -35, lifespan: 500, scale: 1.1 },
  dragon: { sheet: 'dragon-flame', motion: 'burst', count: 7, speed: [50, 120], gravityY: 0, lifespan: 400, scale: 0.8 },
  dark: { sheet: 'claw', motion: 'slash', count: 5, speed: [100, 180], gravityY: 0, lifespan: 300, scale: 0.8 },
  steel: { sheet: 'shrapnel', motion: 'slash', count: 8, speed: [120, 220], gravityY: 60, lifespan: 280, scale: 0.7 },
};

/** Presentation tier: effectiveness picks the weight of the same skin. */
export type FxTier = 'super' | 'neutral' | 'weak';

export interface TierMods {
  countMul: number;
  speedMul: number;
  scaleMul: number;
  /** Weak hits render translucent — the underwhelm cue. */
  alpha: number;
}

export const TIER_MODS: Record<FxTier, TierMods> = {
  super: { countMul: 1.8, speedMul: 1.3, scaleMul: 1.2, alpha: 1 },
  neutral: { countMul: 1, speedMul: 1, scaleMul: 1, alpha: 1 },
  weak: { countMul: 0.4, speedMul: 0.55, scaleMul: 0.8, alpha: 0.6 },
};

/** Hard particle cap per burst — 50fps-on-old-iPhone budget. */
export const PARTICLE_CAP = 20;

/** Freeze window on super/crit impacts. 60-90ms is the classic weight
 *  trick; 80 splits the difference. */
export const HITSTOP_MS = 80;

export interface HitFxPlan {
  skin: TypeFxSkin;
  tier: FxTier;
  mods: TierMods;
  /** Particle count after tier scaling + cap. */
  count: number;
  /** Super-effective OR crit — the big-moment freeze. */
  hitStop: boolean;
}

export type EffectivenessKind = 'super' | 'resisted' | 'immune' | null;

/**
 * Resolve the FX plan for a landing hit. Returns null for immunity
 * (no particles — the "doesn't affect" text is the whole story) and
 * for unknown move types (dex lookup failed — fall back to 'normal'
 * only when the type is affirmatively unknown, not immune).
 */
export function resolveHitFx(
  moveType: string | null,
  eff: EffectivenessKind,
  crit: boolean,
): HitFxPlan | null {
  if (eff === 'immune') return null;
  const type = (moveType ?? 'normal').toLowerCase() as Gen3Type;
  const skin = TYPE_FX[type] ?? TYPE_FX.normal;
  const tier: FxTier =
    eff === 'super' ? 'super' : eff === 'resisted' ? 'weak' : 'neutral';
  const mods = TIER_MODS[tier];
  const count = Math.min(PARTICLE_CAP, Math.max(1, Math.round(skin.count * mods.countMul)));
  return { skin, tier, mods, count, hitStop: eff === 'super' || crit };
}

/**
 * '-12 (34%)' — the user-locked BOTH format. Returns null when no HP
 * actually moved (fail/immune damage lines never reach here, but a
 * 0-delta write must not spawn an empty float). Percent floors at 1
 * so chip damage never reads as '(0%)'; overkill clamps at 100.
 */
export function formatDamageText(
  dmgAbs: number,
  dmgPercent: number,
): string | null {
  if (dmgAbs <= 0) return null;
  const pct = Math.min(100, Math.max(1, Math.round(dmgPercent)));
  return `-${dmgAbs} (${pct}%)`;
}

export type VerdictKind = 'super' | 'weak' | 'crit' | 'immune';

/** Verdict pills for a hit, in display order (effectiveness first,
 *  crit stacked after — matches the cartridge line order). */
export function verdictsFor(
  eff: EffectivenessKind,
  crit: boolean,
): VerdictKind[] {
  const out: VerdictKind[] = [];
  if (eff === 'super') out.push('super');
  else if (eff === 'resisted') out.push('weak');
  else if (eff === 'immune') out.push('immune');
  if (crit && eff !== 'immune') out.push('crit');
  return out;
}

export const VERDICT_LABEL: Record<VerdictKind, string> = {
  super: 'SUPER EFFECTIVE',
  // 'RESISTED', not 'NOT VERY…' — the authored ellipsis read as
  // cut-off text in the round-3 playtest, and it's the dossier's
  // established vocabulary (efficacyChip aria: 'resisted'). The log
  // line still speaks the full cartridge sentence.
  weak: 'RESISTED',
  crit: 'CRITICAL',
  immune: 'NO EFFECT',
};

/**
 * Pill color class from the PLAYER'S perspective (user-locked): your
 * super hit = advantage (--matchup-good); an incoming super = trouble
 * (--matchup-bad); resists invert; immunity is always the ghosted
 * --matchup-none. `target` is the DEFENDER of the hit.
 */
export function verdictClass(
  kind: VerdictKind,
  target: 'p1' | 'p2',
): 'good' | 'bad' | 'none' {
  if (kind === 'immune') return 'none';
  const playerLandedIt = target === 'p2';
  if (kind === 'weak') return playerLandedIt ? 'bad' : 'good';
  return playerLandedIt ? 'good' : 'bad'; // super + crit
}

/** Sanity view for tests: every skin's sheet must exist in the baked
 *  manifest with sane frame data. */
export function skinSheets(): string[] {
  return GEN3_TYPES.map((t) => TYPE_FX[t].sheet);
}

export { BATTLE_FX_MANIFEST };
